import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, Download, TrendingUp, Target, BarChart2, Award, Calendar, Moon, Sun, Zap, Activity, Search, Info, X, Compass, PieChart as PieChartIcon, AlertOctagon, GitMerge, BrainCircuit, Terminal, Flag } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip as RechartsTooltip, AreaChart, Area, XAxis, CartesianGrid, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// --- IMPORT THE BRAIN ---
import { 
  SHIFTS_2026, parseResponseSheet, generateDeepCapColleges, generateNeuralAptitude, 
  getCombatArchetype, generateInsight, predictTotalPercentile, predictRank, predictSubjectPercentile 
} from './utils/parser';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const extractPageText = (textContent) => {
  const rows = new Map();

  textContent.items.forEach((item) => {
    const text = (item.str || '').trim();
    if (!text) return;

    const x = item.transform?.[4] || 0;
    const y = Math.round(item.transform?.[5] || 0);
    const row = rows.get(y) || [];
    row.push({ x, text });
    rows.set(y, row);
  });

  return [...rows.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([, row]) => row.sort((a, b) => a.x - b.x).map(item => item.text).join(' '))
    .join('\n');
};

const extractPdfTextCandidates = async (file) => {
  const readPdf = async (options = {}) => {
    const data = new Uint8Array(await file.arrayBuffer());
    return pdfjsLib.getDocument({ data, ...options }).promise;
  };

  let pdf;
  try {
    pdf = await readPdf();
  } catch (workerError) {
    console.warn('PDF worker failed, retrying without worker:', workerError);
    pdf = await readPdf({ disableWorker: true });
  }

  const pageNumbers = Array.from({ length: pdf.numPages }, (_, index) => index + 1);
  const pagesContent = await Promise.all(
    pageNumbers.map(async (pageNumber) => {
      const page = await pdf.getPage(pageNumber);
      return page.getTextContent();
    })
  );

  const rowOrderedText = pagesContent.map(extractPageText).join('\n');
  const rawOrderedText = pagesContent.map(page => page.items.map(item => item.str).join(' ')).join(' ');

  return [rowOrderedText, rawOrderedText].filter(Boolean);
};

const THEMES = {
  light: {
    bg: '#F5F1EB', surface: 'rgba(255, 255, 255, 0.65)', border: 'rgba(224, 220, 211, 0.8)',
    accent: '#B8956A', accentRGB: '184, 149, 106', textMain: '#2D2D2D', textMuted: '#6B6B6B',
    success: '#16a34a', error: '#ef4444', warning: '#eab308', inputBg: 'rgba(250, 250, 250, 0.8)',
    uploadBg: 'rgba(250, 248, 245, 0.6)', shadow: 'rgba(0, 0, 0, 0.04)'
  },
  dark: {
    bg: '#050505', surface: 'rgba(20, 20, 20, 0.55)', border: 'rgba(42, 42, 42, 0.8)',
    accent: '#A855F7', accentRGB: '168, 85, 247', textMain: '#F5F1EB', textMuted: '#A3A3A3',
    success: '#4ade80', error: '#f87171', warning: '#facc15', inputBg: 'rgba(26, 26, 26, 0.8)',
    uploadBg: 'rgba(17, 17, 17, 0.6)', shadow: 'rgba(0, 0, 0, 0.5)'
  }
};

const GRADIENTS = { light: ['#B8956A', '#E6D5B8'], dark: ['#A855F7', '#D8B4FE'] };

let globalAudioCtx = null;
const playTickSound = () => {
  try {
    if (!globalAudioCtx) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      globalAudioCtx = new AudioContext();
    }
    if (globalAudioCtx.state === 'suspended') globalAudioCtx.resume();
    
    const osc = globalAudioCtx.createOscillator(); 
    const gainNode = globalAudioCtx.createGain();
    osc.type = 'sine'; 
    osc.frequency.setValueAtTime(800, globalAudioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, globalAudioCtx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.05, globalAudioCtx.currentTime); 
    gainNode.gain.exponentialRampToValueAtTime(0.001, globalAudioCtx.currentTime + 0.1);
    osc.connect(gainNode); 
    gainNode.connect(globalAudioCtx.destination);
    osc.start(); 
    osc.stop(globalAudioCtx.currentTime + 0.1);
  } catch (e) {}
};
const triggerHaptic = () => { if (navigator.vibrate) navigator.vibrate(40); };

const SpotlightCard = ({ children, isDarkMode, className, style }) => {
  const divRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  
  const handleMouseMove = (e) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div ref={divRef} onMouseMove={handleMouseMove} onMouseEnter={() => setOpacity(1)} onMouseLeave={() => setOpacity(0)} className={`glass-card hover-lift ${className || ''}`} style={{ ...style, position: 'relative', overflow: 'hidden', border: `1px solid ${isDarkMode ? THEMES.dark.border : THEMES.light.border}`, backgroundColor: isDarkMode ? THEMES.dark.surface : THEMES.light.surface }}>
      <div className="pointer-events-none absolute inset-0 transition-opacity duration-500 no-print pdf-hide-spotlight" style={{ opacity, background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(${isDarkMode ? THEMES.dark.accentRGB : THEMES.light.accentRGB}, 0.12), transparent 40%)`, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
};

const useCountUp = (end, duration = 1500, isDec = false) => {
  const [count, setCount] = useState(0);
  const endVal = end || 0;
  useEffect(() => {
    // Reset to 0 whenever end changes
    setCount(0);
    if (endVal === 0) return;
    let startTime = null;
    let animationFrameId;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Cubic ease-out
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      // Direct interpolation — always reaches exact target
      setCount(easeProgress * endVal);
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(endVal); // Snap to exact value at end
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [endVal, duration]);
  return isDec ? count.toFixed(2) : Math.floor(count);
};

const SerifText = ({ children, style }) => <span style={{ fontFamily: '"Playfair Display", "Georgia", serif', ...style }}>{children}</span>;

const SmoothGradientText = ({ isDark, children, style }) => (
  <span style={{ display: 'inline-grid', ...style }}>
    <span className="gradient-light" style={{ gridArea: '1/1', background: `linear-gradient(to right, ${GRADIENTS.light[0]} 0%, ${GRADIENTS.light[1]} 50%, ${GRADIENTS.light[0]} 100%)`, backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', opacity: isDark ? 0 : 1, transition: 'opacity 0.4s ease-in-out', pointerEvents: isDark ? 'none' : 'auto', animation: 'gradientFlow 4s linear infinite' }}>{children}</span>
    <span className="gradient-dark" style={{ gridArea: '1/1', background: `linear-gradient(to right, ${GRADIENTS.dark[0]} 0%, ${GRADIENTS.dark[1]} 50%, ${GRADIENTS.dark[0]} 100%)`, backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', opacity: isDark ? 1 : 0, transition: 'opacity 0.4s ease-in-out', pointerEvents: isDark ? 'auto' : 'none', animation: 'gradientFlow 4s linear infinite' }}>{children}</span>
  </span>
);

const TerminalLoader = ({ onComplete, t, shouldStop }) => {
  const [lines, setLines] = useState([]);
  // Use a ref so the interval callback always sees the latest onComplete
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const msgs = [
    "> Initializing ZETA Neural Engine...",
    "> Extracting encrypted PDF & MHT nodes...",
    "> Shredding MIME Quoted-Printable Encipherments...",
    "> Cross-referencing 2026 Shift Normalization Tables...",
    "> Applying Provisional Bonus Discrepancies...",
    "> Calculating Gaussian displacement...",
    "> FINALIZING ARRAYS..."
  ];

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (shouldStop) { clearInterval(interval); return; }
      setLines(prev => [...prev, msgs[i]]);
      if (i === 1 || i === 3 || i === 5) playTickSound();
      i++;
      if (i >= msgs.length) {
        clearInterval(interval);
        setTimeout(() => { triggerHaptic(); onCompleteRef.current(); }, 600);
      }
    }, 400);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — stable via ref

  return (
    <div className="glass-card fade-in-up" style={{ backgroundColor: '#050505', border: `1px solid ${t.border}`, borderRadius: '12px', padding: '40px', maxWidth: '600px', margin: '0 auto', fontFamily: 'monospace', color: '#4ade80', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '1px solid #333', paddingBottom: '12px' }}>
        <Terminal size={20} color="#4ade80" />
        <span style={{ fontSize: '14px', fontWeight: 'bold', letterSpacing: '1px' }}>SYSTEM TERMINAL</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px' }}>
        {lines.map((line, index) => <div key={index} className="fade-in-up">{line}</div>)}
        <div className="blinking-cursor">_</div>
      </div>
    </div>
  );
};

const AnimatedDashboard = ({ rawScores, selectedShift, selectedCategory, applyBonus, setApplyBonus, setSelectedCategory, setSelectedShift, handleExportPDF, isExporting, resetApp, t, isDarkMode }) => {
  const [simBranch, setSimBranch] = useState('CS / IT / Cyber Security');
  const [simLoc, setSimLoc] = useState('All');
  const [recoveredSilly, setRecoveredSilly] = useState(0);

  const scores = useMemo(() => {
    if (!rawScores || !rawScores.math) return null;
    const s = JSON.parse(JSON.stringify(rawScores)); 
    const shiftData = SHIFTS_2026.find(sh => sh.id === selectedShift);
    s.shiftData = shiftData;

    if (applyBonus && shiftData?.bonus > 0) {
       s.total += shiftData.bonus; s.math.marks += shiftData.bonus; s.math.correct += (shiftData.bonus / 2);
    }

    if (recoveredSilly > 0) {
       s.total += (recoveredSilly * 1.25); 
       const each = recoveredSilly / 3;
       s.math.marks += each * 1.5; s.physics.marks += each; s.chemistry.marks += each;
    }
    
    const offset = s.shiftData ? s.shiftData.diffOffset : 0;
    s.overallPercentile = predictTotalPercentile(s.total, offset);
    s.predictedRank = predictRank(s.overallPercentile);
    s.math.percentile = predictSubjectPercentile('math', s.math.marks, offset);
    s.physics.percentile = predictSubjectPercentile('physics', s.physics.marks, offset);
    s.chemistry.percentile = predictSubjectPercentile('chemistry', s.chemistry.marks, offset);
    return s;
  }, [rawScores, recoveredSilly, selectedShift, applyBonus]);

  const sData = scores?.shiftData || { difficulty: 'Moderate', diffPct: 50, bonus: 0, bonusMsg: '' };
  
  const animatedTotal = useCountUp(scores?.total || 0, 1200);
  const animatedPercentile = useCountUp(scores?.overallPercentile || 0, 1500, true);
  const animatedRank = useCountUp(scores?.predictedRank || 0, 1800);

  const radarData = [ { subject: 'Mathematics', value: scores?.math?.percentile || 0, fullMark: 100 }, { subject: 'Physics', value: scores?.physics?.percentile || 0, fullMark: 100 }, { subject: 'Chemistry', value: scores?.chemistry?.percentile || 0, fullMark: 100 } ];
  
  const aiProfile = generateNeuralAptitude(scores?.math?.percentile || 0, scores?.physics?.percentile || 0, scores?.chemistry?.percentile || 0);
  const combatArchetype = getCombatArchetype(rawScores);

  const bellData = useMemo(() => {
    let d = []; for(let i=0; i<=200; i+=2) { let val = Math.exp(-Math.pow(i - 85, 2)/(2 * Math.pow(30, 2))); d.push({ score: i, density: val * 100 }); }
    return d;
  }, []);
  
  const outscoredCount = Math.round(320000 * ((scores?.overallPercentile || 0) / 100));

  const mistakeSplit = useMemo(() => {
    if (!rawScores || !rawScores.physics) return [];
    const incorrect = rawScores.physics.incorrect + rawScores.chemistry.incorrect + rawScores.math.incorrect;
    if(incorrect === 0) return [];
    const sillyPct = Math.max(0.2, Math.min(0.6, rawScores.total / 300)); 
    const silly = Math.round(incorrect * sillyPct); const conceptual = incorrect - silly;
    return [ { name: 'Calculation/Silly Errors', value: silly, color: t.warning }, { name: 'Conceptual Gaps', value: conceptual, color: t.error } ];
  }, [rawScores, t]);
  
  const maxSillyMistakes = mistakeSplit.length > 0 ? mistakeSplit[0].value : 0;
  const capColleges = generateDeepCapColleges(scores?.predictedRank || 1, selectedCategory, simBranch, simLoc);

  if (!rawScores || !rawScores.math || !scores) {
    return (
      <div className="glass-card fade-in-up" style={{ padding: '40px', textAlign: 'center', backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px' }}>
        <AlertTriangle size={32} color={t.error} style={{ margin: '0 auto 16px auto' }} />
        <h3 style={{ color: t.textMain, margin: '0 0 10px 0' }}>Data Fragment Missing</h3>
        <p style={{ color: t.textMuted, fontSize: '14px', marginBottom: '20px' }}>Your previous session data was corrupted. Let's start fresh.</p>
        <button onClick={resetApp} className="hover-lift" style={{ backgroundColor: t.accent, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Upload New File</button>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="no-print cinematic-fade-1" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', marginBottom: '24px' }}>
        <button onClick={resetApp} className="hover-lift" style={{ backgroundColor: 'transparent', color: t.textMuted, border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', padding: 0 }}>← Upload New</button>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select className="hover-lift no-print" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '13px', color: t.textMain, fontWeight: '600', cursor: 'pointer' }}>
            <option value="Open">Category: Open</option><option value="OBC">Category: OBC/EWS</option>
            <option value="SC">Category: SC</option><option value="ST">Category: ST</option>
          </select>
          <select className="hover-lift no-print" value={selectedShift} onChange={(e) => setSelectedShift(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '13px', color: t.textMain, fontWeight: '600', cursor: 'pointer' }}>
            {SHIFTS_2026.map(shift => (<option key={shift.id} value={shift.id}>{shift.label}</option>))}
          </select>
          <button className="hover-lift" onClick={handleExportPDF} disabled={isExporting} style={{ backgroundColor: t.accent, color: isDarkMode ? '#111' : '#fff', border: 'none', borderRadius: '6px', cursor: isExporting ? 'wait' : 'pointer', fontSize: '13px', fontWeight: '600', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px', opacity: isExporting ? 0.7 : 1 }}>
            <Download size={16} /> {isExporting ? "GENERATING..." : "EXPORT PDF"}
          </button>
        </div>
      </div>

      <div id="dashboard-to-export" style={{ padding: '20px', margin: '-20px', backgroundColor: t.bg }}>
        
        <div className="cinematic-fade-2 glass-card hover-lift" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', padding: '16px 24px', borderRadius: '12px', backgroundColor: t.inputBg, border: `1px solid ${combatArchetype.color}40` }}>
           <div style={{ padding: '12px', borderRadius: '50%', backgroundColor: `${combatArchetype.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {combatArchetype.icon}
           </div>
           <div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: combatArchetype.color, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Combat Archetype Profile</div>
              <div style={{ fontSize: '18px', fontWeight: '800', color: t.textMain }}>{combatArchetype.name}</div>
              <div style={{ fontSize: '13px', color: t.textMuted, marginTop: '2px' }}>{combatArchetype.desc}</div>
           </div>
        </div>

        {sData.bonus > 0 && (
          <SpotlightCard isDarkMode={isDarkMode} className="cinematic-fade-2" style={{ padding: '16px 20px', borderRadius: '12px', border: `1px solid ${t.warning}`, marginBottom: '24px', backgroundColor: isDarkMode ? 'rgba(250, 204, 21, 0.1)' : 'rgba(234, 179, 8, 0.05)' }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                   <AlertOctagon size={24} color={t.warning} />
                   <div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: t.warning, textTransform: 'uppercase', letterSpacing: '1px' }}>Official Discrepancy Detected</div>
                      <div style={{ fontSize: '14px', color: t.textMain }}>{sData.bonusMsg}</div>
                   </div>
                </div>
                <button onClick={() => setApplyBonus(!applyBonus)} className="hover-lift no-print" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '6px', backgroundColor: applyBonus ? t.success : t.inputBg, color: applyBonus ? '#fff' : t.textMain, border: `1px solid ${applyBonus ? t.success : t.border}`, cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.3s' }}>
                   {applyBonus ? <CheckCircle size={16} /> : <XCircle size={16} />}
                   {applyBonus ? "Bonus Applied" : `Simulate +${sData.bonus} Marks`}
                </button>
             </div>
          </SpotlightCard>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 2fr) minmax(250px, 1fr)', gap: '20px', marginBottom: '24px' }} className="responsive-grid cinematic-fade-3">
          <div className="glass-card" style={{ backgroundColor: t.uploadBg, borderLeft: `4px solid ${t.accent}`, padding: '20px', borderRadius: '0 12px 12px 0', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Zap size={20} color={t.accent} style={{ marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: t.accent, letterSpacing: '1px', marginBottom: '6px' }}>Algorithmic Insight</div>
              <div style={{ fontSize: '14px', color: t.textMain, lineHeight: '1.6' }}>{generateInsight(scores)}</div>
            </div>
          </div>
          
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '20px', borderRadius: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMain, fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
              <Activity size={18} color={t.accent} /> Shift Difficulty Barometer
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '700', color: t.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <span>Easy</span><span style={{ color: t.textMain }}>{sData.difficulty}</span><span>Brutal</span>
            </div>
            <div style={{ width: '100%', height: '8px', backgroundColor: t.inputBg, borderRadius: '10px', overflow: 'hidden', border: `1px solid ${t.border}` }}>
              <div style={{ width: `${sData.diffPct}%`, height: '100%', background: `linear-gradient(90deg, ${t.success}, ${t.warning}, ${t.error})`, borderRadius: '10px', transition: 'width 1s ease-out' }} />
            </div>
          </SpotlightCard>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '20px' }} className="cinematic-fade-4">
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '24px', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMuted, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}><BarChart2 size={16} color={t.accent} /> Raw Score</div>
            <div style={{ fontSize: '48px', color: t.textMain, lineHeight: '1', fontWeight: '300' }}>{animatedTotal} <span style={{ fontSize: '16px', color: t.textMuted }}>/ 200</span></div>
          </SpotlightCard>
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '24px', borderRadius: '12px', border: `1px solid ${t.accent}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.accent, fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}><TrendingUp size={16} /> Est. Percentile</div>
            <div style={{ fontSize: '48px', lineHeight: '1', fontWeight: '600' }}><SmoothGradientText isDark={isDarkMode}>{animatedPercentile}</SmoothGradientText><span style={{ fontSize: '16px', color: t.textMuted, fontWeight: '400', marginLeft: '6px' }}>%ile</span></div>
          </SpotlightCard>
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '24px', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMuted, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}><Award size={16} color={t.accent} /> Expected Rank</div>
            <div style={{ fontSize: '48px', color: t.textMain, lineHeight: '1', fontWeight: '600' }}>{animatedRank.toLocaleString('en-IN')}</div>
          </SpotlightCard>
        </div>

        <SpotlightCard isDarkMode={isDarkMode} className="cinematic-fade-5" style={{ padding: '24px', borderRadius: '12px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMain, fontSize: '14px', fontWeight: '600', marginBottom: '24px' }}>
            <Compass size={18} color={t.accent} /> Gaussian Dominance Curve
          </div>
          <div style={{ width: '100%', height: '220px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={bellData} margin={{ top: 30, right: 20, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="colorDensity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={t.accent} stopOpacity={0.6}/>
                    <stop offset="95%" stopColor={t.accent} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
                <XAxis dataKey="score" stroke={t.textMuted} tick={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="density" stroke={t.accent} strokeWidth={2} fillOpacity={1} fill="url(#colorDensity)" />
                <ReferenceLine x={scores.total || 0} stroke={t.success} strokeDasharray="4 4" label={{ position: 'top', value: 'You', fill: t.success, fontSize: 16, fontWeight: '800', dy: -10 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '15px', color: t.textMuted }}>
            Your performance statistically bypassed <strong style={{ color: t.textMain, fontSize: '18px' }}>{outscoredCount.toLocaleString('en-IN')}</strong> candidates globally.
          </div>
        </SpotlightCard>

        {/* NEURAL CAREER AI MODULE */}
        <SpotlightCard isDarkMode={isDarkMode} className="cinematic-fade-5" style={{ padding: '20px', borderRadius: '12px', marginBottom: '24px', border: `1px solid ${t.accent}` }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', padding: '12px', borderRadius: '50%' }}><BrainCircuit size={28} color={t.accent} /></div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '800', color: t.accent, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Neural Branch-Mapping AI</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: t.textMain, marginBottom: '6px' }}>{aiProfile.title}</div>
              <div style={{ fontSize: '14px', color: t.textMuted, lineHeight: '1.5', marginBottom: '12px' }}>{aiProfile.desc}</div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: t.textMain }}>High-Aptitude Sectors: <span style={{ color: t.accent }}>{aiProfile.branches}</span></div>
            </div>
          </div>
        </SpotlightCard>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 1fr)', gap: '20px', marginBottom: '20px' }} className="responsive-grid cinematic-fade-6">
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '24px', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px', color: t.textMain, fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}><Activity size={18} color={t.accent} /> PCM Mastery Shape</div>
            <div className="radar-wrapper" style={{ width: '100%', height: '320px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="65%" data={radarData} margin={{ top: 10, right: 50, bottom: 10, left: 50 }}>
                  <PolarGrid stroke={t.border} />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: t.textMuted, fontSize: 13, fontWeight: 600 }} />
                  <RechartsTooltip contentStyle={{ backgroundColor: t.inputBg, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.textMain }} itemStyle={{ color: t.accent, fontWeight: 700 }} />
                  <Radar name="Percentile" dataKey="value" stroke={t.accent} fill={t.accent} fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </SpotlightCard>
          
          <SpotlightCard isDarkMode={isDarkMode} style={{ padding: '24px', borderRadius: '12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMain, fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}><PieChartIcon size={18} color={t.accent} /> Mistake Analysis Breakdown</div>
            {mistakeSplit.length > 0 ? (
              <div style={{ width: '100%', height: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ width: '100%', height: '200px', position: 'relative' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={mistakeSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} stroke={isDarkMode ? THEMES.dark.surface : THEMES.light.surface} strokeWidth={3} paddingAngle={5}>
                        {mistakeSplit.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip contentStyle={{ backgroundColor: t.inputBg, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.textMain }} itemStyle={{ color: t.textMain, fontWeight: 600 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px', fontSize: '12px', fontWeight: '600', color: t.textMuted }}>
                  {mistakeSplit.map((m, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: m.color }}/>{m.name}</div>)}
                </div>
                
                <div className="no-print" style={{ marginTop: '20px', padding: '16px', backgroundColor: t.inputBg, borderRadius: '8px', border: `1px solid ${t.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: t.textMain, display: 'flex', alignItems: 'center', gap: '6px' }}><GitMerge size={14} color={t.accent}/> The Butterfly Effect</span>
                    <span style={{ fontSize: '12px', color: t.accent, fontWeight: '600' }}>Recover {recoveredSilly} / {maxSillyMistakes}</span>
                  </div>
                  <input type="range" min="0" max={maxSillyMistakes} value={recoveredSilly} 
                    onChange={(e) => { 
                      const val = Number(e.target.value);
                      if (val !== recoveredSilly) { setRecoveredSilly(val); playTickSound(); }
                    }} 
                    className="time-machine-slider hover-lift" style={{ width: '100%', cursor: 'pointer' }} 
                  />
                  <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '8px', textAlign: 'center' }}>Drag to simulate correcting silly mistakes.</div>
                </div>
              </div>
            ) : (
              <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textMuted, fontSize: '14px' }}>Perfect accuracy. No mistakes logged.</div>
            )}
          </SpotlightCard>
        </div>

        <SpotlightCard isDarkMode={isDarkMode} className="cinematic-fade-6" style={{ padding: '24px', borderRadius: '12px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: t.textMain, fontSize: '16px', fontWeight: '600' }}><Target size={20} color={t.accent} /> Deep-DTE Probabilistic Mapping</div>
            <div className="no-print" style={{ display: 'flex', gap: '12px' }}>
              <select className="hover-lift" value={simBranch} onChange={e => setSimBranch(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '12px', color: t.textMain, fontWeight: '600' }}>
                <option value="CS / IT / Cyber Security">Branch: CS / IT / Cyber</option>
                <option value="AI / Data Science">Branch: AI / Data Science</option>
                <option value="ENTC / Electronics">Branch: ENTC / Electronics</option>
                <option value="Electrical / Inst">Branch: Electrical / Inst</option>
                <option value="Core (Mech/Civil/Chem)">Branch: Core (Mech/Civil/Chem)</option>
              </select>
              <select className="hover-lift" value={simLoc} onChange={e => setSimLoc(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '12px', color: t.textMain, fontWeight: '600' }}>
                <option value="All">Region: All</option>
                <option value="Mumbai">Region: Mumbai</option>
                <option value="Pune">Region: Pune</option>
                <option value="Other">Region: Rest of MAH</option>
              </select>
            </div>
          </div>

          <div className="no-print" style={{ marginBottom: '30px', padding: '20px', backgroundColor: t.inputBg, borderRadius: '8px', border: `1px solid ${t.border}`, position: 'relative' }}>
             <div style={{ fontSize: '12px', fontWeight: '700', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>Ghost Racing Track (CAP R1)</div>
             <div style={{ position: 'relative', width: '100%', height: '4px', backgroundColor: isDarkMode ? '#333' : '#ddd', borderRadius: '4px', margin: '20px 0' }}>
                {capColleges.map((c, i) => {
                   const pos = Math.max(0, Math.min(100, (1 - (c.adjCutoff / 80000)) * 100));
                   return (
                      <div key={i} style={{ position: 'absolute', left: `${pos}%`, top: '-18px', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                         <Flag size={12} color={t.textMuted} />
                         <div style={{ width: '2px', height: '8px', backgroundColor: t.textMuted, marginTop: '2px' }}/>
                         <div style={{ fontSize: '9px', fontWeight: 'bold', color: t.textMuted, marginTop: '4px', whiteSpace: 'nowrap', position: 'absolute', top: '20px' }}>{c.name.split(',')[0]}</div>
                      </div>
                   )
                })}
                <div style={{ position: 'absolute', left: `${Math.max(0, Math.min(100, (1 - ((scores?.predictedRank || 1) / 80000)) * 100))}%`, top: '-6px', transform: 'translateX(-50%)', transition: 'left 0.4s cubic-bezier(0.25, 1, 0.5, 1)', zIndex: 10 }}>
                   <div style={{ width: '16px', height: '16px', backgroundColor: t.accent, borderRadius: '50%', border: `3px solid ${t.surface}`, boxShadow: `0 0 12px ${t.accent}` }} />
                </div>
             </div>
          </div>
          
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: '700px', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}`, color: t.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '12px 16px', fontWeight: '600', width: '25%' }}>Institute Target</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600', width: '15%' }}>Probability</th>
                  <th style={{ padding: '12px 16px', fontWeight: '600', width: '60%' }}>Mathematical Seat Analysis</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '13px' }}>
                {capColleges.map((clg, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ padding: '16px', fontWeight: '700', color: t.textMain }}>{clg.name}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '800', backgroundColor: clg.status === 'Dream' ? 'rgba(239,68,68,0.1)' : clg.status === 'Target' ? 'rgba(234,179,8,0.1)' : 'rgba(22,163,74,0.1)', color: clg.status === 'Dream' ? t.error : clg.status === 'Target' ? t.warning : t.success }}>{clg.prob}%</span>
                    </td>
                    <td style={{ padding: '16px', color: t.textMuted, fontWeight: '500', lineHeight: '1.5' }}>{clg.probText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SpotlightCard>

        <div className="glass-card cinematic-fade-7" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '40px' }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${t.border}`, backgroundColor: t.inputBg }}><h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: t.textMain }}>Performance Telemetry</h3></div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}`, color: t.textMuted, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '16px 24px', fontWeight: '600' }}>Subject</th><th style={{ padding: '16px 24px', fontWeight: '600' }}>Accuracy Profile</th><th style={{ padding: '16px 24px', fontWeight: '700', color: t.textMain }}>Marks</th><th style={{ padding: '16px 24px', fontWeight: '700', color: t.accent }}>%ile</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '14px' }}>
                {[{ title: 'Mathematics', data: scores?.math, max: 100 }, { title: 'Physics', data: scores?.physics, max: 50 }, { title: 'Chemistry', data: scores?.chemistry, max: 50 }].map((sub, idx) => {
                  const attempted = (sub.data?.correct || 0) + (sub.data?.incorrect || 0); const accuracy = attempted === 0 ? 0 : ((sub.data?.correct || 0) / attempted) * 100;
                  return (
                    <tr key={idx} style={{ borderBottom: `1px solid ${t.border}` }}>
                      <td style={{ padding: '20px 24px', fontWeight: '600', color: t.textMain }}>{sub.title}</td>
                      <td style={{ padding: '20px 24px', width: '40%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}><span style={{ color: t.success }}>{sub.data?.correct || 0} Correct</span><span style={{ color: t.error }}>{sub.data?.incorrect || 0} Incorrect</span></div>
                        <div style={{ width: '100%', height: '6px', backgroundColor: t.inputBg, borderRadius: '10px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${accuracy}%`, backgroundColor: t.accent, borderRadius: '10px', transition: 'width 1.5s cubic-bezier(0.16, 1, 0.3, 1)', animation: 'fillBar 1.5s ease-out forwards' }} /></div>
                      </td>
                      <td style={{ padding: '20px 24px', fontWeight: '700', fontSize: '16px', color: t.textMain }}>{sub.data?.marks || 0} <span style={{ fontSize: '12px', color: t.textMuted, fontWeight: '400' }}>/ {sub.max}</span></td>
                      <td style={{ padding: '20px 24px', fontWeight: '700', fontSize: '16px', color: t.accent }}>{(sub.data?.percentile || 0).toFixed(2)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedShift, setSelectedShift] = useState('15_1'); 
  const [selectedCategory, setSelectedCategory] = useState('Open');
  const [applyBonus, setApplyBonus] = useState(false);
  
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isTerminalRunning, setIsTerminalRunning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [parseErrorMessage, setParseErrorMessage] = useState('');
  const [baseScores, setBaseScores] = useState(null); 
  const [isExporting, setIsExporting] = useState(false);
  const [isCmdKOpen, setIsCmdKOpen] = useState(false);
  const [cmdSearch, setCmdSearch] = useState('');
  const [isTrustOpen, setIsTrustOpen] = useState(false);

  // --- Race-condition fix: track parse & terminal state independently ---
  const parseStatusRef = useRef('idle'); // 'idle' | 'done' | 'error'
  const terminalDoneRef = useRef(false);
  const [terminalShouldStop, setTerminalShouldStop] = useState(false);
  
  const t = isDarkMode ? THEMES.dark : THEMES.light;

  useEffect(() => {
    const savedTheme = localStorage.getItem('zetaTheme');
    if (savedTheme === 'dark') setIsDarkMode(true);
    
    const savedResults = localStorage.getItem('zetaLastResult');
    if (savedResults && savedResults !== "null") { 
      try { 
        const parsed = JSON.parse(savedResults);
        if (parsed && typeof parsed === 'object') {
          setBaseScores(parsed); 
          setHasCalculated(true); 
        }
      } catch (e) {} 
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setIsCmdKOpen(prev => !prev); }
      if (e.key === 'Escape') { setIsCmdKOpen(false); setIsTrustOpen(false); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleTheme = () => { setIsDarkMode(!isDarkMode); localStorage.setItem('zetaTheme', !isDarkMode ? 'dark' : 'light'); };

  const handleFileUpload = async (event) => {
    const input = event.target;
    const file = input.files[0]; if (!file) return;
    // Reset state
    setHasError(false); setParseErrorMessage(''); setApplyBonus(false);
    parseStatusRef.current = 'idle';
    terminalDoneRef.current = false;
    setTerminalShouldStop(false);

    const failUpload = (message) => {
      parseStatusRef.current = 'error';
      setTerminalShouldStop(true);
      setParseErrorMessage(message);
      setHasError(true);
      setIsTerminalRunning(false);
      input.value = '';
    };

    if (file.size === 0) {
      failUpload('This file is empty (0 KB). Please re-download the response sheet or use the non-empty HTML/MHTML saved page.');
      return;
    }

    setIsTerminalRunning(true);

    // Yield thread so Terminal mounts + renders first
    await new Promise(resolve => setTimeout(resolve, 80)); 

    try {
      let content = "";
      let textCandidates = [];

      // --- Detect PDF by extension, MIME type, or magic bytes (works for all uploads) ---
      const lowerName = file.name.toLowerCase();
      const isPDFByMeta = lowerName.endsWith('.pdf') || file.type === 'application/pdf';
      let isPDF = isPDFByMeta;
      if (!isPDF) {
        // Check magic bytes as fallback
        try {
          const headerBytes = file.slice(0, 5);
          const headerText = await new Promise(resolve => {
            const r = new FileReader(); r.onload = e => resolve(e.target.result); r.readAsText(headerBytes);
          });
          if (headerText.startsWith('%PDF')) isPDF = true;
        } catch (_) {}
      }
      
      if (isPDF) {
        // --- PDF path: use pdfjs ---
        textCandidates = await extractPdfTextCandidates(file);
        content = textCandidates[0] || '';
      } else {
        // --- Text path: HTML, MHT, MHTML, TXT, etc. ---
        const bytes = await file.arrayBuffer();
        const decodeWith = (encoding) => {
          try { return new TextDecoder(encoding).decode(bytes); } catch (_) { return ''; }
        };
        textCandidates = [
          decodeWith('utf-8'),
          decodeWith('utf-16le'),
          decodeWith('utf-16be'),
          decodeWith('windows-1252')
        ].filter(Boolean);

        content = textCandidates.find(text => /Correct\s*(?:Option|Answer|Response|Ans)|MIME-Version:|multipart\/(?:related|mixed|alternative)|<html/i.test(text)) || textCandidates[0] || '';
      }
      
      const parseCandidates = [content, ...textCandidates].filter((text, index, arr) => text && arr.indexOf(text) === index);
      const parsed = parseCandidates
        .map(text => ({ text, result: parseResponseSheet(text) }))
        .find(item => item.result && item.result.totalParsedQuestions > 0);
      const results = parsed?.result;
      
      if (!results || results.totalParsedQuestions === 0) {
        failUpload('Document not recognized. Upload the saved response sheet page that contains Correct Option and Candidate Response rows.');
        return;
        // Parsing failed — signal terminal to stop, show error
      }

      results.rawContent = parsed.text;
      const savedResults = { ...results };
      delete savedResults.rawContent;
      try {
        localStorage.setItem('zetaLastResult', JSON.stringify(savedResults));
      } catch (storageError) {
        console.warn("Could not persist result locally:", storageError);
      }
      setBaseScores(results);
      parseStatusRef.current = 'done';

      // If terminal already finished while we were parsing, go to dashboard now
      if (terminalDoneRef.current) {
        setIsTerminalRunning(false);
        setHasCalculated(true);
      }
      // Otherwise, terminal's onComplete will fire next and pick up 'done' status

    } catch (err) {
      console.error("Parse Error:", err);
      failUpload('Could not read this file. Please try the HTML/MHTML response sheet export or re-download the file.');
    }
  };

  const handleTerminalComplete = useCallback(() => {
    terminalDoneRef.current = true;
    if (parseStatusRef.current === 'done') {
      // Parsing already finished — go straight to dashboard
      setIsTerminalRunning(false);
      setHasCalculated(true);
    } else if (parseStatusRef.current === 'error') {
      // Already handled in handleFileUpload
      setIsTerminalRunning(false);
    }
    // If 'idle': parsing is still running — handleFileUpload will detect terminalDoneRef
    // and call setHasCalculated(true) itself when it finishes
  }, []);

  const resetApp = () => {
    parseStatusRef.current = 'idle';
    terminalDoneRef.current = false;
    setTerminalShouldStop(false);
    setHasCalculated(false);
    setHasError(false);
    setParseErrorMessage('');
    setBaseScores(null);
    setIsTerminalRunning(false);
    localStorage.removeItem('zetaLastResult');
  };

  const handleExportPDF = async () => {
    const el = document.getElementById('dashboard-to-export');
    if (!el) return;
    setIsExporting(true); playTickSound(); triggerHaptic();
    try {
      el.classList.add('is-printing');
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: isDarkMode ? '#050505' : '#F5F1EB', useCORS: true, logging: false });
      el.classList.remove('is-printing');
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width / 2, canvas.height / 2] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save('ZETA-MHTCET-Scorecard.pdf');
    } catch (error) {}
    setIsExporting(false);
  };

  const commands = [
    { label: `${isDarkMode ? 'Disable' : 'Enable'} Dark Mode`, icon: isDarkMode ? <Sun size={16}/> : <Moon size={16}/>, action: toggleTheme },
    { label: 'View Trust Center (Algorithm Math)', icon: <Info size={16}/>, action: () => setIsTrustOpen(true) },
    ...(hasCalculated ? [{ label: 'Export PDF Scorecard', icon: <Download size={16}/>, action: handleExportPDF }, { label: 'Reset & Clear Local Data', icon: <AlertTriangle size={16}/>, action: resetApp }] : [])
  ].filter(c => c.label.toLowerCase().includes(cmdSearch.toLowerCase()));

  return (
    <>
      <style>{`
        @keyframes gradientFlow { 0% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes fillBar { 0% { transform: scaleX(0); transform-origin: left; } 100% { transform: scaleX(1); transform-origin: left; } }
        
        /* OPTIMIZED CINEMATIC REVEAL ANIMATIONS (No Blur) */
        @keyframes cinematicFadeIn { 0% { opacity: 0; transform: translateY(30px); } 100% { opacity: 1; transform: translateY(0); } }
        .cinematic-fade-1 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 0.1s; }
        .cinematic-fade-2 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 0.3s; }
        .cinematic-fade-3 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 0.5s; }
        .cinematic-fade-4 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 0.7s; }
        .cinematic-fade-5 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 0.9s; }
        .cinematic-fade-6 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 1.1s; }
        .cinematic-fade-7 { animation: cinematicFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; animation-delay: 1.3s; }
        
        .fade-in-up { animation: cinematicFadeIn 0.6s cubic-bezier(0.25, 1, 0.5, 1) forwards; opacity: 0; }
        .delay-1 { animation-delay: 0.1s; } .delay-3 { animation-delay: 0.3s; }
        
        .hover-lift { transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease, border-color 0.4s ease; }
        .hover-lift:hover { transform: translateY(-4px); box-shadow: 0 12px 40px ${t.shadow}; }

        .glass-card { backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
        .ambient-glow { position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: -1; background: radial-gradient(circle at 50% 0%, rgba(${t.accentRGB}, 0.08) 0%, transparent 70%); }
        
        /* TIME MACHINE SLIDER CSS */
        .time-machine-slider { -webkit-appearance: none; appearance: none; height: 6px; background: ${t.border}; border-radius: 5px; outline: none; transition: 0.2s; }
        .time-machine-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; background: ${t.accent}; cursor: pointer; border: 2px solid ${t.surface}; box-shadow: 0 0 10px rgba(${t.accentRGB}, 0.5); }

        /* HACKER TERMINAL CSS */
        .blinking-cursor { display: inline-block; width: 10px; height: 18px; background-color: #4ade80; animation: blink 1s step-end infinite; margin-top: 4px; }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

        .radar-wrapper .recharts-surface { overflow: visible !important; }
        body { background-color: ${t.bg}; transition: background-color 0.5s ease, color 0.5s ease; margin: 0; }
        @media (max-width: 768px) { .responsive-grid { grid-template-columns: 1fr !important; } }
        
        /* FLAWLESS HTML2CANVAS CSS OVERRIDE */
        .is-printing .gradient-light, .is-printing .gradient-dark { display: inline !important; background: none !important; -webkit-text-fill-color: ${isDarkMode ? '#F5F1EB' : '#2D2D2D'} !important; color: ${isDarkMode ? '#F5F1EB' : '#2D2D2D'} !important; opacity: 1 !important; animation: none !important; }
        .is-printing .pdf-hide-spotlight, .is-printing .cinematic-fade-1, .is-printing .cinematic-fade-2, .is-printing .cinematic-fade-3, .is-printing .cinematic-fade-4, .is-printing .cinematic-fade-5, .is-printing .cinematic-fade-6, .is-printing .cinematic-fade-7 { animation: none !important; opacity: 1 !important; transform: none !important; filter: none !important; }
        .is-printing .glass-card { border: 1px solid ${isDarkMode ? '#333' : '#ddd'} !important; box-shadow: none !important; transform: none !important; }
        select:focus { outline: 2px solid ${t.accent}; border-color: ${t.accent}; }

        /* Cmd+K Modal */
        .cmd-modal-overlay { position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,0.4); backdrop-filter: blur(8px); z-index: 999; display: flex; justify-content: center; align-items: flex-start; padding-top: 10vh; }
        .cmd-modal { width: 90%; max-width: 500px; background: ${t.surface}; border: 1px solid ${t.border}; border-radius: 12px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
        .cmd-input { width: 100%; padding: 20px; font-size: 16px; background: transparent; border: none; border-bottom: 1px solid ${t.border}; color: ${t.textMain}; outline: none; }
        .cmd-option { padding: 16px 20px; display: flex; align-items: center; gap: 12px; cursor: pointer; color: ${t.textMuted}; transition: all 0.2s ease; }
        .cmd-option:hover { background: ${t.inputBg}; color: ${t.textMain}; }
      `}</style>

      {/* --- COMMAND PALETTE (CMD+K) --- */}
      {isCmdKOpen && (
        <div className="cmd-modal-overlay no-print" onClick={() => setIsCmdKOpen(false)}>
          <div className="cmd-modal fade-in-up" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', borderBottom: `1px solid ${t.border}` }}>
              <Search size={18} color={t.textMuted} />
              <input autoFocus value={cmdSearch} onChange={e => setCmdSearch(e.target.value)} placeholder="Type a command or search..." className="cmd-input" style={{ borderBottom: 'none' }} />
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {commands.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: t.textMuted, fontSize: '14px' }}>No commands found.</div>
              ) : commands.map((cmd, i) => (
                <div key={i} className="cmd-option" onClick={() => { cmd.action(); setIsCmdKOpen(false); setCmdSearch(''); }}>
                  {cmd.icon} <span style={{ fontSize: '14px', fontWeight: '500' }}>{cmd.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- TRUST CENTER MODAL --- */}
      {isTrustOpen && (
        <div className="cmd-modal-overlay no-print" onClick={() => setIsTrustOpen(false)} style={{ alignItems: 'center', paddingTop: 0 }}>
          <div className="cmd-modal fade-in-up" style={{ maxWidth: '600px', padding: '30px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: t.textMain, fontSize: '20px' }}><SerifText>Trust Center: Calculation Engine</SerifText></h2>
              <button onClick={() => setIsTrustOpen(false)} style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer' }}><X size={20}/></button>
            </div>
            <p style={{ color: t.textMuted, fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
              <strong>1. Shift Normalization:</strong> Raw scores are offset using historical difficulty matrices mapped from MHT CET session data. (e.g. April 15 Shift 1 is recognized as the toughest).<br/><br/>
              <strong>2. Gaussian Distribution:</strong> Standing is calculated against a simulated pool of 3.2 Lakh candidates based on real-world percentile distributions.<br/><br/>
              <strong>3. CAP Round Simulation:</strong> Ranks are adjusted dynamically using a strict Category Multiplier before running predictions against historical DTE cutoff databases.
            </p>
            <button onClick={() => setIsTrustOpen(false)} className="hover-lift" style={{ width: '100%', padding: '12px', background: t.inputBg, border: `1px solid ${t.border}`, color: t.textMain, borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Acknowledge</button>
          </div>
        </div>
      )}

      <div className="ambient-glow" />

      <div className="print-container" style={{ minHeight: '100vh', fontFamily: '"Inter", sans-serif', color: t.textMain, padding: '40px 20px', display: 'flex', flexDirection: 'column' }}>
        <header className="fade-in-up" style={{ maxWidth: '1000px', width: '100%', margin: '0 auto 40px auto', paddingBottom: '20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', letterSpacing: '1px' }}>
              <SerifText style={{ color: t.textMain, fontWeight: '700' }}>ZETA <SmoothGradientText isDark={isDarkMode} style={{ fontWeight: '400' }}>Predictor</SmoothGradientText></SerifText>
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div className="no-print" style={{ fontSize: '12px', color: t.textMuted, border: `1px solid ${t.border}`, padding: '4px 10px', borderRadius: '20px', backgroundColor: t.surface }}>Press <kbd style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>⌘K</kbd></div>
            <button className="hover-lift no-print" onClick={toggleTheme} style={{ background: 'transparent', border: `1px solid ${t.border}`, borderRadius: '50%', padding: '8px', cursor: 'pointer', color: t.textMain, zIndex: 50 }}>
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>

        <main style={{ maxWidth: '1000px', width: '100%', margin: '0 auto', flexGrow: 1 }}>
          {isTerminalRunning ? (
            <TerminalLoader t={t} onComplete={handleTerminalComplete} shouldStop={terminalShouldStop} />
          ) : !hasCalculated ? (
            <div className="card glass-card fade-in-up delay-1" style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', padding: '60px 40px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '32px', fontWeight: '400', marginBottom: '12px', color: t.textMain }}><SerifText>Analyze Your Response Sheet</SerifText></h2>
              <p style={{ color: t.textMuted, fontSize: '15px', marginBottom: '40px' }}>Configure your exam settings below, then securely upload your document.</p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px', maxWidth: '600px', margin: '0 auto 30px auto', textAlign: 'left' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: t.textMain, marginBottom: '10px' }}><Calendar size={18} color={t.accent} /> Exam Shift</label>
                  <select className="hover-lift" value={selectedShift} onChange={(e) => setSelectedShift(e.target.value)} style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '15px', color: t.textMain }}>
                    {SHIFTS_2026.map(shift => (<option key={shift.id} value={shift.id}>{shift.label}</option>))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: t.textMain, marginBottom: '10px' }}><Target size={18} color={t.accent} /> Category</label>
                  <select className="hover-lift" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={{ width: '100%', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${t.border}`, backgroundColor: t.inputBg, fontSize: '15px', color: t.textMain }}>
                    <option value="Open">Open / General</option><option value="OBC">OBC / EWS</option>
                    <option value="SC">SC</option><option value="ST">ST</option>
                  </select>
                </div>
              </div>

              {hasError && (
                <div className="fade-in-up" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: `1px solid ${t.error}`, color: t.error, padding: '12px', borderRadius: '8px', marginBottom: '20px', maxWidth: '600px', margin: '0 auto 20px auto', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <AlertTriangle size={18} /> {parseErrorMessage || 'Document not recognized. Please upload the response sheet file.'}
                </div>
              )}

              <div className="hover-lift" style={{ position: 'relative', border: `2px dashed ${t.accent}`, borderRadius: '12px', backgroundColor: t.uploadBg, padding: '50px 20px', maxWidth: '600px', margin: '0 auto', cursor: 'pointer' }}>
                <input type="file" onChange={handleFileUpload} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                <UploadCloud color={t.accent} size={48} style={{ marginBottom: '16px' }} />
                <div style={{ fontSize: '16px', color: t.textMain, fontWeight: '600' }}>Click to browse or drag file here</div>
              </div>
            </div>
          ) : (
            <AnimatedDashboard 
              rawScores={baseScores} 
              selectedShift={selectedShift} 
              selectedCategory={selectedCategory} 
              applyBonus={applyBonus} 
              setApplyBonus={setApplyBonus} 
              setSelectedCategory={setSelectedCategory} 
              setSelectedShift={setSelectedShift} 
              handleExportPDF={handleExportPDF} 
              isExporting={isExporting} 
              resetApp={resetApp} 
              t={t} 
              isDarkMode={isDarkMode} 
            />
          )}
        </main>

        <footer className="no-print fade-in-up delay-3" style={{ textAlign: 'center', padding: '40px 0 20px 0', marginTop: 'auto' }}>
          <button className="hover-lift" onClick={() => setIsTrustOpen(true)} style={{ background: 'none', border: `1px solid ${t.border}`, color: t.textMuted, fontSize: '12px', cursor: 'pointer', padding: '8px 16px', borderRadius: '20px', fontWeight: '500', zIndex: 50 }}>How is this calculated?</button>
        </footer>
      </div>
    </>
  );
}

export default App;
